import { useQuery } from '@tanstack/react-query';

import { getFeatureRecord } from 'api/jalapeno';

import useAccountQuery from './useAccountQuery';

export const FEATURE_RECORD_QUERY_KEY = 'featureRecord';

// White list of feature keys that are used in the feature record API.
// To add a new feature, you need to add the key to the backend whitelist also.
export const FEATURE_KEYS = {
    ONBOARDING_OPEN_MODAL_VIEWED: 'onboarding_modal_viewed',
    ONBOARDING_CLOSED_MODAL_VIEWED: 'onboarding_closed_modal_viewed',
    PLUS_MEMBERSHIP_OPT_IN_PILL_VIEWED: 'plus_membership_opt_in_pill_viewed',
};

/**
 * Check if a feature should be shown based on the feature record data.
 * @param {Object} featureRecord - The feature record data from the API.
 * @param {string} featureKey - The feature key to check.
 * @returns {boolean} - Whether the feature should be shown.
 *
 * Returns true (show the feature) only if:
 * - featureKey exists in featureRecord with a falsy value (not yet seen)
 *
 * Returns false (don't show) if:
 * - featureRecord is not available
 * - featureKey is not whitelisted on the backend (not in featureRecord)
 * - featureKey has already been seen (has a truthy value)
 */
export function shouldSeeFeature(featureRecord, featureKey) {
    if (!featureRecord || typeof featureRecord !== 'object') {
        return false;
    }
    // If the key doesn't exist in featureRecord, backend doesn't have it whitelisted
    if (!(featureKey in featureRecord)) {
        return false;
    }
    return !featureRecord[featureKey];
}

const useFeatureRecordQuery = () => {
    const { data: account } = useAccountQuery();
    const chargeID = account?.nextOrder?.chargeID;

    const query = useQuery({
        queryKey: [FEATURE_RECORD_QUERY_KEY, account?.subID, chargeID],
        queryFn: async () => {
            const response = await getFeatureRecord(account?.subID, chargeID);
            return response?.data?.results;
        },
        enabled: !!account?.subID,
    });

    return query;
};

export default useFeatureRecordQuery;
